import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { PortalService, UtilsService } from '../../../services/index';
import { SnackBar } from '../../../enums/index';
import { CONSTANTS_TOKEN } from '../../../config/index';

export interface GrantAccessDialogData {
  selectedDocuments: any[];
  source?: 'document-browser' | 'selected-documents-viewer';
  isRevokeMode?: boolean;
}

export interface GrantAccessDialogResult {
  success: boolean;
}

@Component({
  selector: 'educare-grant-access-dialog',
  templateUrl: './grant-access-dialog.component.html',
  styleUrl: './grant-access-dialog.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatRadioModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslateModule
  ]
})
export class GrantAccessDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<GrantAccessDialogComponent>);
  public readonly data = inject<GrantAccessDialogData>(MAT_DIALOG_DATA);
  private readonly portalService = inject(PortalService);
  private readonly apiConstants = inject(CONSTANTS_TOKEN);
  private readonly utils = inject(UtilsService);
  accessType: 'group' | 'user' = 'group';
  searchTerm = '';
  loading = false;
  submitting = false;
  selectedGroups: any[] = [];
  selectedUsers: any[] = [];
  groups: any[] = [];
  users: any[] = [];
  filteredItems: any[] = [];
  currentPage = 1;
  hasMoreData = true;
  isLoadingMore = false;
  existingUsers: any[] = [];
  existingGroups: any[] = [];
  loadingExistingAccess = false;
  isRevokeMode: boolean = this.data.isRevokeMode ?? false;

  ngOnInit() {
    if (this.isRevokeMode) {
      this.loadExistingAccess();
    } else {
      // Load existing access first to filter out already granted users/groups
      this.loadExistingAccessThenLoadItems();
    }
  }

  /**
   * Load existing access list first, then load groups/users with filtering
   */
  private loadExistingAccessThenLoadItems() {
    this.loading = true;
    const payload = { document_ids: this.data.selectedDocuments.map((doc) => doc.id) };
    const url = this.utils.getEncryptedUrls(this.apiConstants.api.privateEndpoints.getAccessList);

    this.portalService.post(url, payload).subscribe({
      next: (response: any) => {
        this.existingUsers = response.data?.users || [];
        this.existingGroups = response.data?.groups || [];
        this.loadGroups();
      },
      error: () => {
        // Even if fetching existing access fails, continue loading groups/users
        this.existingUsers = [];
        this.existingGroups = [];
        this.loadGroups();
      }
    });
  }

  private loadExistingAccess() {
    this.loadingExistingAccess = true;
    const payload = { document_ids: this.data.selectedDocuments.map((doc) => doc.id) };
    const url = this.utils.getEncryptedUrls(this.apiConstants.api.privateEndpoints.getAccessList);

    this.portalService.post(url, payload).subscribe({
      next: (response: any) => {
        this.existingUsers = response.data?.users || [];
        this.existingGroups = response.data?.groups || [];
        this.loadingExistingAccess = false;
      },
      error: (error) => {
        this.utils.openSnackBar(error?.error?.message, 'Close', SnackBar.Error);
        this.loadingExistingAccess = false;
      }
    });
  }

  onAccessTypeChange(type: 'group' | 'user') {
    this.accessType = type;
    this.searchTerm = '';

    if (type === 'group') {
      this.loadGroups();
    } else {
      this.loadUsers();
    }
  }

  loadGroups() {
    this.loading = true;
    this.currentPage = 1;
    this.hasMoreData = true;
    this.groups = [];
    this.loadGroupsPage(1);
  }

  private loadGroupsPage(page: number) {
    if (!this.apiConstants?.api.privateEndpoints?.groups) {
      this.loading = false;
      return;
    }

    const params = new URLSearchParams({
      page: page.toString(),
      limit: '100'
    });

    const url = `${this.utils.getEncryptedUrls(this.apiConstants.api.privateEndpoints.groups)}?${params.toString()}`;

    this.portalService.get(url).subscribe({
      next: (response: any) => {
        const newGroups = response.data?.groups || response.data || [];

        if (page === 1) {
          this.groups = newGroups;
        } else {
          this.groups = [...this.groups, ...newGroups];
        }
        // Filter out groups that already have access (only in grant mode, not revoke mode)
        this.filteredItems = this.filterOutExistingAccess(this.groups, 'group');
        this.hasMoreData = newGroups.length === 100;
        this.currentPage = page;
        this.loading = false;
        this.isLoadingMore = false;
      },
      error: (error) => {
        this.utils.openSnackBar(error?.error?.message, 'Close', SnackBar.Error);
        this.loading = false;
        this.isLoadingMore = false;
      }
    });
  }

  loadUsers() {
    this.loading = true;
    this.currentPage = 1;
    this.hasMoreData = true;
    this.users = [];

    this.loadUsersPage(1);
  }

  private loadUsersPage(page: number) {
    if (!this.apiConstants?.api.privateEndpoints?.users) {
      this.loading = false;
      return;
    }

    const params = new URLSearchParams({
      page: page.toString(),
      limit: '100',
      is_profile_completed: '1'
    });

    const url = `${this.utils.getEncryptedUrls(this.apiConstants.api.privateEndpoints.users)}${params.toString()}`;

    this.portalService.get(url).subscribe({
      next: (response: any) => {
        const newUsers = response.data?.user || [];

        if (page === 1) {
          this.users = newUsers;
        } else {
          this.users = [...this.users, ...newUsers];
        }

        // Filter out users that already have access (only in grant mode, not revoke mode)
        this.filteredItems = this.filterOutExistingAccess(this.users, 'user');
        this.hasMoreData = newUsers.length === 100;
        this.currentPage = page;
        this.loading = false;
        this.isLoadingMore = false;
      },
      error: (error) => {
        this.utils.openSnackBar(error?.error?.message, 'Close', SnackBar.Error);
        this.loading = false;
        this.isLoadingMore = false;
      }
    });
  }

  onSearch() {
    const items = this.accessType === 'group' ? this.groups : this.users;
    const searchKey = this.accessType === 'group' ? 'group_name' : 'full_name';

    // First filter by search term
    const filtered = items.filter((item) => item[searchKey]?.toLowerCase().includes(this.searchTerm.toLowerCase()));

    // Then filter out items that already have access (only in grant mode)
    this.filteredItems = this.filterOutExistingAccess(filtered, this.accessType);
  }

  /**
   * Filter out users or groups that already have access to the selected documents
   * Only applies in grant mode, not revoke mode
   */
  private filterOutExistingAccess(items: any[], type: 'group' | 'user'): any[] {
    // In revoke mode, don't filter - show all items
    if (this.isRevokeMode) {
      return [...items];
    }

    const existingIds = type === 'group' ? this.existingGroups.map((g) => g.id) : this.existingUsers.map((u) => u.id);

    // If no existing access data, return all items
    if (existingIds.length === 0) {
      return [...items];
    }

    // Filter out items that already have access
    return items.filter((item) => !existingIds.includes(item.id));
  }

  toggleItemSelection(item: any, checked: boolean) {
    if (this.accessType === 'group') {
      if (checked) {
        this.selectedGroups.push(item);
      } else {
        this.selectedGroups = this.selectedGroups.filter((selected) => selected.id !== item.id);
      }
    } else {
      if (checked) {
        this.selectedUsers.push(item);
      } else {
        this.selectedUsers = this.selectedUsers.filter((selected) => selected.id !== item.id);
      }
    }
  }

  isItemSelected(item: any): boolean {
    if (this.accessType === 'group') {
      return this.selectedGroups.some((selected) => selected.id === item.id);
    } else {
      return this.selectedUsers.some((selected) => selected.id === item.id);
    }
  }

  removeSelectedItem(item: any) {
    if (this.accessType === 'group') {
      this.selectedGroups = this.selectedGroups.filter((selected) => selected.id !== item.id);
    } else {
      this.selectedUsers = this.selectedUsers.filter((selected) => selected.id !== item.id);
    }
  }

  get selectedItems(): any[] {
    return this.accessType === 'group' ? this.selectedGroups : this.selectedUsers;
  }

  get existingItems(): any[] {
    return this.accessType === 'group' ? this.existingGroups : this.existingUsers;
  }

  onGrantAccess() {
    this.submitting = true;
    const payload: any = {
      documents: this.data.selectedDocuments.map((doc) => ({
        id: doc.id,
        name: doc.name || this.utils.translation('common.unknown'),
        type: doc.type === 'folder' ? 'folder' : 'file'
      })),
      group_ids: this.selectedGroups.map((group) => parseInt(group.id)),
      user_ids: this.selectedUsers.map((user) => parseInt(user.id))
    };

    this.portalService
      .post(this.utils.getEncryptedUrls(this.apiConstants.api.privateEndpoints.grantAccess), payload)
      .subscribe({
        next: () => {
          this.utils.openSnackBar(this.utils.translation('snackbar.accessGrantedSuccess'), 'Close', SnackBar.Success);
          this.dialogRef.close({ success: true });
        },
        error: (error) => {
          this.utils.openSnackBar(error?.error?.message, 'Close', SnackBar.Error);
          this.submitting = false;
        }
      });
  }

  onCancel() {
    this.dialogRef.close({ success: false });
  }

  onScroll(event: any) {
    const element = event.target;
    const threshold = 50;

    if (element.scrollHeight - element.scrollTop <= element.clientHeight + threshold) {
      this.loadMore();
    }
  }

  loadMore() {
    if (!this.hasMoreData || this.isLoadingMore) return;

    this.isLoadingMore = true;
    const nextPage = this.currentPage + 1;

    if (this.accessType === 'group') {
      this.loadGroupsPage(nextPage);
    } else {
      this.loadUsersPage(nextPage);
    }
  }

  revokeAccess() {
    this.submitting = true;
    const payload = {
      document_ids: this.data.selectedDocuments.map((doc) => doc.id),
      user_ids: this.selectedUsers.map((user) => parseInt(user.id)),
      group_ids: this.selectedGroups.map((group) => parseInt(group.id))
    };

    this.portalService
      .post(this.utils.getEncryptedUrls(this.apiConstants.api.privateEndpoints.revokeAccess), payload)
      .subscribe({
        next: () => {
          this.selectedUsers = [];
          this.selectedGroups = [];

          this.utils.openSnackBar(this.utils.translation('snackbar.accessRevokedSuccess'), 'Close', SnackBar.Success);
          this.submitting = false;
          this.dialogRef.close({ success: true });
        },
        error: (error) => {
          this.utils.openSnackBar(error?.error?.message, 'Close', SnackBar.Error);
          this.submitting = false;
        }
      });
  }

  removeDocument(document: any) {
    const index = this.data.selectedDocuments.findIndex((doc) => doc.id === document.id);
    if (index > -1) {
      this.data.selectedDocuments.splice(index, 1);
      this.loadExistingAccess();
    }

    if (this.data.selectedDocuments.length === 0) {
      this.utils.openSnackBar(this.utils.translation('snackbar.noDocumentsSelected'), 'Close', SnackBar.Error);
      this.dialogRef.close({ success: false });
    }
  }
}
